import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import {
    ConversationMessageRow,
    ConversationUsageRow,
    PageResponse,
    UserAiUsage,
    fetchUserAiConversation,
    fetchUserAiConversations,
    fetchUserAiUsage,
} from "../services/polarisService";
import "../styles/polaris-admin.css";

function formatUsd(value: number | undefined): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
}

function formatConversationWhen(value: string | undefined): string {
    if (!value) {
        return "";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(parsed);
}

function TokenGauge({ value, max }: { value: number; max: number }) {
    const safeMax = Math.max(max, value, 1);
    const pct = Math.min(100, (value / safeMax) * 100);
    const r = 42;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return (
        <div className="pa-token-gauge" aria-label={`Lifetime tokens ${value.toLocaleString()}`}>
            <svg viewBox="0 0 100 100" width="140" height="140">
                <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
                <circle
                    cx="50"
                    cy="50"
                    r={r}
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="10"
                    strokeDasharray={circ}
                    strokeDashoffset={offset}
                    transform="rotate(-90 50 50)"
                />
                <text x="50" y="48" textAnchor="middle" fontSize="12" fontWeight="700">
                    {value.toLocaleString()}
                </text>
                <text x="50" y="64" textAnchor="middle" fontSize="8" fill="#6b7280">
                    tokens
                </text>
            </svg>
        </div>
    );
}

export default function PolarisUserAiUsagePage() {
    const { userId } = useParams();
    const id = Number(userId);
    const [usage, setUsage] = useState<UserAiUsage | null>(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState<PageResponse<ConversationUsageRow> | null>(null);
    const [filters, setFilters] = useState({ from: "", to: "", minTokens: "", maxTokens: "", minCostUsd: "", maxCostUsd: "" });
    const [debounced, setDebounced] = useState(filters);
    const [error, setError] = useState<string | null>(null);
    const [messages, setMessages] = useState<ConversationMessageRow[] | null>(null);
    const [activeConversation, setActiveConversation] = useState<ConversationUsageRow | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(filters), 300);
        return () => window.clearTimeout(t);
    }, [filters]);

    const load = useCallback(async () => {
        if (!Number.isFinite(id)) return;
        setError(null);
        try {
            const toIso = (value: string): string | undefined => {
                if (!value) return undefined;
                const parsed = new Date(value);
                return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
            };
            const [user, conversations] = await Promise.all([
                fetchUserAiUsage(id),
                fetchUserAiConversations(id, {
                    page,
                    size: 10,
                    from: toIso(debounced.from),
                    to: toIso(debounced.to),
                    minTokens: debounced.minTokens,
                    maxTokens: debounced.maxTokens,
                    minCostUsd: debounced.minCostUsd,
                    maxCostUsd: debounced.maxCostUsd,
                }),
            ]);
            setUsage(user);
            setRows(conversations);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load AI usage.");
        }
    }, [debounced, id, page]);

    useEffect(() => {
        void load();
    }, [load]);

    const closeConversation = useCallback(() => {
        setMessages(null);
        setActiveConversation(null);
    }, []);

    const openConversation = async (row: ConversationUsageRow) => {
        try {
            const detail = await fetchUserAiConversation(id, row.sessionId);
            setActiveConversation(row);
            setMessages(detail);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load that conversation.");
        }
    };

    useEffect(() => {
        if (!messages) {
            return undefined;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeConversation();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [closeConversation, messages]);

    return (
        <section className="pa-section" data-testid="polaris-user-ai-usage-page">
            <Link to="/polaris-admin/users" className="pa-back-link">
                Back to users
            </Link>
            <h2 className="pa-section-title">{usage ? `${usage.displayName} · AI usage` : "AI usage"}</h2>
            {usage && <p className="pa-muted">{usage.email}</p>}
            {error && <div className="pa-error">{error}</div>}

            {usage && (
                <div className="pa-assist-user-hero">
                    <TokenGauge
                        value={usage.lifetimeTokens}
                        max={Math.max(usage.lifetimeTokens, usage.effectiveLimits.monthlyTokenLimit)}
                    />
                    <div>
                        <div className="stat-label">Lifetime cost</div>
                        <div className="stat-value">{formatUsd(usage.lifetimeCostUsd)}</div>
                    </div>
                </div>
            )}

            <table className="pa-table">
                <thead>
                    <tr>
                        <th>Date / time</th>
                        <th>Conversation tokens</th>
                        <th>Cost</th>
                    </tr>
                    <tr className="pa-filter-row">
                        <th>
                            <input
                                className="pa-filter-input"
                                type="datetime-local"
                                value={filters.from}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, from: e.target.value }));
                                }}
                                aria-label="Filter from date"
                            />
                            <input
                                className="pa-filter-input"
                                type="datetime-local"
                                value={filters.to}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, to: e.target.value }));
                                }}
                                aria-label="Filter to date"
                            />
                        </th>
                        <th>
                            <input
                                className="pa-filter-input"
                                placeholder="Min tokens"
                                value={filters.minTokens}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, minTokens: e.target.value }));
                                }}
                            />
                            <input
                                className="pa-filter-input"
                                placeholder="Max tokens"
                                value={filters.maxTokens}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, maxTokens: e.target.value }));
                                }}
                            />
                        </th>
                        <th>
                            <input
                                className="pa-filter-input"
                                placeholder="Min $"
                                value={filters.minCostUsd}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, minCostUsd: e.target.value }));
                                }}
                            />
                            <input
                                className="pa-filter-input"
                                placeholder="Max $"
                                value={filters.maxCostUsd}
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters((f) => ({ ...f, maxCostUsd: e.target.value }));
                                }}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {(rows?.content ?? []).length === 0 ? (
                        <tr>
                            <td colSpan={3} style={{ textAlign: "center", padding: 24 }}>
                                No conversations found
                            </td>
                        </tr>
                    ) : (
                        rows?.content.map((row) => (
                            <tr
                                key={row.sessionId}
                                className="pa-clickable-row"
                                onClick={() => void openConversation(row)}
                            >
                                <td>{row.updatedAt}</td>
                                <td>{row.totalTokens.toLocaleString()}</td>
                                <td>{formatUsd(row.costUsd)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {rows && rows.totalPages > 1 && (
                <div className="pa-pagination">
                    <button className="pa-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                        Previous
                    </button>
                    <span>
                        Page {page + 1} of {rows.totalPages}
                    </span>
                    <button
                        className="pa-page-btn"
                        disabled={page + 1 >= rows.totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next
                    </button>
                </div>
            )}

            {messages && (
                <div
                    className="pa-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="pa-conversation-title"
                    onClick={closeConversation}
                >
                    <div
                        className="pa-modal pa-conversation-modal"
                        data-testid="polaris-user-ai-conversation-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="pa-modal-close"
                            aria-label="Close conversation"
                            onClick={closeConversation}
                        >
                            ×
                        </button>
                        <header className="pa-conversation-header">
                            <p className="pa-conversation-kicker">Conversation</p>
                            <h3 id="pa-conversation-title" className="pa-conversation-title">
                                {activeConversation?.title?.trim() || "Untitled conversation"}
                            </h3>
                            <p className="pa-conversation-meta">
                                {[
                                    formatConversationWhen(
                                        activeConversation?.updatedAt ?? messages[0]?.createdAt,
                                    ),
                                    activeConversation
                                        ? `${activeConversation.totalTokens.toLocaleString()} tokens`
                                        : null,
                                    activeConversation ? formatUsd(activeConversation.costUsd) : null,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </p>
                        </header>
                        <div className="pa-conversation-thread">
                            {messages.map((msg, i) => {
                                const isUser = msg.role === "user";
                                return (
                                    <article
                                        key={`${msg.createdAt}-${i}`}
                                        className={isUser ? "pa-thread-user" : "pa-thread-assistant"}
                                    >
                                        <header className="pa-thread-meta">
                                            <span className="pa-thread-role">
                                                {isUser ? "User" : "Polaris Assist"}
                                            </span>
                                            {msg.createdAt ? (
                                                <time className="pa-thread-time" dateTime={msg.createdAt}>
                                                    {formatConversationWhen(msg.createdAt)}
                                                </time>
                                            ) : null}
                                        </header>
                                        {isUser ? (
                                            <p className="pa-thread-body">{msg.content}</p>
                                        ) : (
                                            <div className="pa-thread-body">
                                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                        <div className="pa-modal-actions">
                            <button type="button" className="pa-page-btn" onClick={closeConversation}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
